import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Trash2,
  Edit3,
  GraduationCap,
  BookOpen,
  Calendar,
  User,
  Loader2,
  CheckCircle,
  AlertCircle,
  Briefcase,
  Target,
} from "lucide-react";

export default function KelolaTutor() {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI STATE (Toast & Modal)
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success",
  });
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    id: null,
    nama: "",
  });

  // FORM DATA
  const [formData, setFormData] = useState({
    nama: "",
    mapel: "", // Sesuai DB: mapel
    tanggal_lahir: "",
    pendidikan: "",
    pengalaman: "",
    fokus: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchTutors();
  }, []);

  const showToast = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification((prev) => ({ ...prev, show: false })),
      3000
    );
  };

  async function fetchTutors() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("tutor")
        .select("*")
        .order("nama_lengkap", { ascending: true });

      if (error) throw error;
      setTutors(data || []);
    } catch (error) {
      showToast("Gagal memuat data: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  }

  async function handleSimpan(e) {
    e.preventDefault();
    if (!formData.nama || !formData.mapel) {
      return showToast("Nama dan Mata Pelajaran wajib diisi!", "error");
    }

    try {
      // PERBAIKAN DI SINI: Menggunakan 'mapel' bukan 'mata_pelajaran'
      const payload = {
        nama_lengkap: formData.nama,
        mapel: formData.mapel, // <--- NAMA KOLOM DB: mapel
        tanggal_lahir: formData.tanggal_lahir,
        pendidikan_terakhir: formData.pendidikan,
        pengalaman: formData.pengalaman,
        fokus: formData.fokus,
      };

      if (isEditing) {
        const { error } = await supabase
          .from("tutor")
          .update(payload)
          .eq("id", editId);
        if (error) throw error;
        showToast("Data tutor berhasil diperbarui!", "success");
      } else {
        const { error } = await supabase.from("tutor").insert([payload]);
        if (error) throw error;
        showToast("Tutor baru berhasil ditambahkan!", "success");
      }

      resetForm();
      fetchTutors();
    } catch (error) {
      showToast("Gagal menyimpan: " + error.message, "error");
    }
  }

  function clickHapus(item) {
    setDeleteModal({ show: true, id: item.id, nama: item.nama_lengkap });
  }

  async function confirmHapus() {
    try {
      const { error } = await supabase
        .from("tutor")
        .delete()
        .eq("id", deleteModal.id);
      if (error) throw error;

      showToast("Tutor berhasil dihapus.", "success");
      fetchTutors();
    } catch (error) {
      showToast("Gagal menghapus: " + error.message, "error");
    } finally {
      setDeleteModal({ show: false, id: null, nama: "" });
    }
  }

  function handleEdit(item) {
    // PERBAIKAN DI SINI JUGA: item.mapel
    setFormData({
      nama: item.nama_lengkap,
      mapel: item.mapel, // <--- AMBIL DARI KOLOM mapel
      tanggal_lahir: item.tanggal_lahir || "",
      pendidikan: item.pendidikan_terakhir,
      pengalaman: item.pengalaman || "",
      fokus: item.fokus || "",
    });
    setIsEditing(true);
    setEditId(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function resetForm() {
    setFormData({
      nama: "",
      mapel: "",
      tanggal_lahir: "",
      pendidikan: "",
      pengalaman: "",
      fokus: "",
    });
    setIsEditing(false);
    setEditId(null);
  }

  const formatTanggal = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  if (loading)
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader2 className="animate-spin" size={40} color="#2563eb" />
      </div>
    );

  // --- STYLES ---
  const styles = {
    container: {
      maxWidth: "900px",
      margin: "0 auto",
      padding: "20px",
      fontFamily: "sans-serif",
      background: "#f8fafc",
      minHeight: "100vh",
    },
    card: {
      background: "white",
      padding: "30px",
      borderRadius: "16px",
      boxShadow: "0 4px 6px rgba(0,0,0,0.02)",
      marginBottom: "25px",
      border: "1px solid #e2e8f0",
    },
    label: {
      display: "block",
      marginBottom: "8px",
      fontWeight: "bold",
      fontSize: "11px",
      color: "#64748b",
      textTransform: "uppercase",
    },
    input: {
      width: "100%",
      padding: "12px 12px 12px 40px",
      borderRadius: "10px",
      border: "1px solid #cbd5e1",
      outline: "none",
      boxSizing: "border-box",
      fontSize: "14px",
    },
    textarea: {
      width: "100%",
      padding: "12px",
      borderRadius: "10px",
      border: "1px solid #cbd5e1",
      outline: "none",
      boxSizing: "border-box",
      fontSize: "14px",
      minHeight: "80px",
      fontFamily: "sans-serif",
    },
    inputIcon: {
      position: "absolute",
      top: "12px",
      left: "12px",
      color: "#94a3b8",
    },
    btnPrimary: {
      width: "100%",
      padding: "12px",
      borderRadius: "10px",
      border: "none",
      background: "#2563eb",
      color: "white",
      fontWeight: "bold",
      cursor: "pointer",
      display: "flex",
      justifyContent: "center",
      gap: "8px",
    },
    btnCancel: {
      width: "100px",
      padding: "12px",
      borderRadius: "10px",
      border: "none",
      background: "#94a3b8",
      color: "white",
      fontWeight: "bold",
      cursor: "pointer",
    },

    toast: {
      position: "fixed",
      top: notification.show ? "30px" : "-80px",
      left: "50%",
      transform: "translateX(-50%)",
      background: "white",
      padding: "12px 24px",
      borderRadius: "50px",
      boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      zIndex: 9999,
      border:
        notification.type === "success"
          ? "1px solid #dcfce7"
          : "1px solid #fee2e2",
      transition: "all 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55)",
      opacity: notification.show ? 1 : 0,
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      background: "rgba(15, 23, 42, 0.6)",
      backdropFilter: "blur(4px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 10000,
      opacity: deleteModal.show ? 1 : 0,
      visibility: deleteModal.show ? "visible" : "hidden",
      transition: "all 0.2s ease-in-out",
    },
    modalCard: {
      background: "white",
      padding: "30px",
      borderRadius: "20px",
      width: "90%",
      maxWidth: "350px",
      textAlign: "center",
      transform: deleteModal.show ? "scale(1)" : "scale(0.95)",
      transition: "all 0.2s",
    },
  };

  return (
    <div style={styles.container}>
      {/* TOAST & MODAL */}
      <div style={styles.toast}>
        {notification.type === "success" ? (
          <CheckCircle size={20} color="#22c55e" />
        ) : (
          <AlertCircle size={20} color="#ef4444" />
        )}
        <span style={{ fontSize: "14px", fontWeight: "600", color: "#1e293b" }}>
          {notification.message}
        </span>
      </div>

      <div style={styles.modalOverlay}>
        <div style={styles.modalCard}>
          <div
            style={{
              width: 60,
              height: 60,
              background: "#FEF2F2",
              borderRadius: "50%",
              margin: "0 auto 20px auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Trash2 size={30} color="#EF4444" />
          </div>
          <h3 style={{ margin: "0 0 10px 0", color: "#1E293B" }}>
            Hapus Tutor?
          </h3>
          <p style={{ color: "#64748B", fontSize: "14px", marginBottom: 25 }}>
            Menghapus <strong>"{deleteModal.nama}"</strong> akan menghilangkan
            data relasi dan jadwalnya.
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() =>
                setDeleteModal({ show: false, id: null, nama: "" })
              }
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "white",
                fontWeight: "bold",
                cursor: "pointer",
                color: "#64748b",
              }}
            >
              Batal
            </button>
            <button
              onClick={confirmHapus}
              style={{
                flex: 1,
                padding: 12,
                borderRadius: 10,
                border: "none",
                background: "#EF4444",
                color: "white",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Hapus
            </button>
          </div>
        </div>
      </div>

      <Link
        to="/"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "5px",
          textDecoration: "none",
          color: "#64748b",
          marginBottom: "20px",
        }}
      >
        <ArrowLeft size={18} /> Kembali
      </Link>

      <h2
        style={{
          color: "#1e293b",
          marginBottom: 20,
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <GraduationCap size={28} color="#2563eb" /> Kelola Tutor
      </h2>

      {/* FORM INPUT */}
      <div style={styles.card}>
        <h3
          style={{
            marginTop: 0,
            color: "#334155",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: 20,
          }}
        >
          {isEditing ? <Edit3 size={18} /> : <User size={18} />}
          {isEditing ? "Edit Data Tutor" : "Tambah Tutor Baru"}
        </h3>

        <form onSubmit={handleSimpan}>
          <div style={{ marginBottom: "15px" }}>
            <label style={styles.label}>Nama Lengkap</label>
            <div style={{ position: "relative" }}>
              <User size={18} style={styles.inputIcon} />
              <input
                style={styles.input}
                placeholder="Nama Lengkap Tutor"
                value={formData.nama}
                onChange={(e) =>
                  setFormData({ ...formData, nama: e.target.value })
                }
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: "200px", marginBottom: "15px" }}>
              <label style={styles.label}>Mata Pelajaran</label>
              <div style={{ position: "relative" }}>
                <BookOpen size={18} style={styles.inputIcon} />
                <input
                  style={styles.input}
                  placeholder="Contoh: Matematika"
                  value={formData.mapel}
                  onChange={(e) =>
                    setFormData({ ...formData, mapel: e.target.value })
                  }
                />
              </div>
            </div>

            <div style={{ flex: 1, minWidth: "200px", marginBottom: "15px" }}>
              <label style={styles.label}>Pendidikan Terakhir</label>
              <div style={{ position: "relative" }}>
                <GraduationCap size={18} style={styles.inputIcon} />
                <select
                  style={{ ...styles.input, background: "white" }}
                  value={formData.pendidikan}
                  onChange={(e) =>
                    setFormData({ ...formData, pendidikan: e.target.value })
                  }
                >
                  <option value="">-- Pilih --</option>
                  <option value="S1 Pendidikan">S1 Pendidikan</option>
                  <option value="S1 Non-Pendidikan">S1 Non-Pendidikan</option>
                  <option value="S2">S2 Magister</option>
                  <option value="S3">S3 Doktor</option>
                  <option value="Lainnya">Lainnya</option>
                </select>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label style={styles.label}>Tanggal Lahir</label>
            <div style={{ position: "relative" }}>
              <Calendar size={18} style={styles.inputIcon} />
              <input
                type="date"
                style={styles.input}
                value={formData.tanggal_lahir}
                onChange={(e) =>
                  setFormData({ ...formData, tanggal_lahir: e.target.value })
                }
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "15px",
              flexWrap: "wrap",
              marginBottom: "25px",
            }}
          >
            <div style={{ flex: 1, minWidth: "300px" }}>
              <label style={styles.label}>Pengalaman Mengajar</label>
              <textarea
                style={styles.textarea}
                placeholder="Ceritakan pengalaman mengajar..."
                value={formData.pengalaman}
                onChange={(e) =>
                  setFormData({ ...formData, pengalaman: e.target.value })
                }
              />
            </div>
            <div style={{ flex: 1, minWidth: "300px" }}>
              <label style={styles.label}>Fokus Belajar / Keahlian</label>
              <textarea
                style={styles.textarea}
                placeholder="Spesialisasi atau metode mengajar..."
                value={formData.fokus}
                onChange={(e) =>
                  setFormData({ ...formData, fokus: e.target.value })
                }
              />
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button type="submit" style={styles.btnPrimary}>
              <Save size={18} />{" "}
              {isEditing ? "Simpan Perubahan" : "Simpan Data"}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                style={styles.btnCancel}
              >
                Batal
              </button>
            )}
          </div>
        </form>
      </div>

      {/* LIST TUTOR */}
      <h3 style={{ color: "#334155", fontSize: 16, marginBottom: 15 }}>
        Daftar Tutor ({tutors.length})
      </h3>

      {tutors.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            background: "white",
            borderRadius: "16px",
            border: "1px dashed #cbd5e1",
            color: "#94a3b8",
          }}
        >
          <User size={40} style={{ opacity: 0.3, marginBottom: 10 }} />
          <p>Belum ada data tutor.</p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "15px",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
          }}
        >
          {tutors.map((item) => (
            <div
              key={item.id}
              style={{
                background: "white",
                padding: "20px",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: "15px",
                  alignItems: "center",
                  marginBottom: 15,
                }}
              >
                <div
                  style={{
                    width: 50,
                    height: 50,
                    background: "#eff6ff",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#2563eb",
                  }}
                >
                  <User size={24} />
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      color: "#1e293b",
                    }}
                  >
                    {item.nama_lengkap}
                  </div>
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#64748b",
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <BookOpen size={12} /> {item.mapel} {/* TAMPILKAN MAPEL */}
                  </div>
                </div>
              </div>

              <div
                style={{
                  background: "#f8fafc",
                  padding: "10px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#475569",
                  display: "grid",
                  gap: 6,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <GraduationCap size={14} color="#94a3b8" />
                  <span>{item.pendidikan_terakhir || "-"}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Calendar size={14} color="#94a3b8" />
                  <span>{formatTanggal(item.tanggal_lahir)}</span>
                </div>
              </div>

              {/* Tampilan Data Tambahan */}
              {(item.pengalaman || item.fokus) && (
                <div style={{ marginTop: 10, fontSize: 12, color: "#64748b" }}>
                  {item.fokus && (
                    <div style={{ display: "flex", gap: 5, marginBottom: 4 }}>
                      <Target
                        size={12}
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />{" "}
                      {item.fokus}
                    </div>
                  )}
                  {item.pengalaman && (
                    <div style={{ display: "flex", gap: 5 }}>
                      <Briefcase
                        size={12}
                        style={{ flexShrink: 0, marginTop: 2 }}
                      />{" "}
                      <span
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "200px",
                        }}
                      >
                        {item.pengalaman}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: "flex", gap: "8px", marginTop: "15px" }}>
                <button
                  onClick={() => handleEdit(item)}
                  style={{
                    flex: 1,
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    background: "white",
                    color: "#d97706",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Edit3 size={16} />
                </button>
                <button
                  onClick={() => clickHapus(item)}
                  style={{
                    flex: 1,
                    padding: "8px",
                    borderRadius: "8px",
                    border: "1px solid #fee2e2",
                    background: "#fff1f2",
                    color: "#ef4444",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
